import { faClipboardCheck, faGift, faMugHot, faRepeat } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { UncontrolledTooltip } from "reactstrap";
import Avatar from "../../../../../../components/Avatar";
import { renderUserName } from "../../../../../../utils/helpers/user";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { useResetOverride } from "./api";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";

function Overrides({ user, data, date, refetch }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { trigger, isMutating } = useResetOverride(user.id);
    const reset = useCallback(async (key) => {
        try {
            await trigger({ rule: key, date });
            toast.success(t("reset-success"));
            refetch?.();
        } catch (error) {
            getErrorMessage(error, t);
        }
    }, [trigger, refetch, t, date]);

    const rules = useMemo(() => [
        {
            key: "automaticBreaks",
            label: t("automatic-breaks"),
            icon: faMugHot,
        },
        {
            key: "guaranteeRule",
            label: t("guarantee-rules"),
            icon: faClipboardCheck
        },
        {
            key: "holidayRules",
            label: t("holiday-rules"),
            icon: faGift
        }
    ], [t]);

    const overrides = useMemo(() => {
        return data?.filter((item) => moment(item.date).isSame(moment(date), "date"));
    }, [data, date]);

    const hasOverrides = useMemo(() => {
        return overrides?.some((item) => {
            return rules.some((rule) => item?.[rule.key]);
        });
    }, [overrides, rules]);

    if (!hasOverrides) {
        return (
            <div className="text-center text-muted py-4">
                {t("no-data")}
            </div>
        );
    }

    return overrides.map((item, i) => (
        <div key={i}>
            {rules.map(({ key, label, icon }) => {
                if (!item?.[key]?.updatedAt) return null;

                const tooltipId = `override-${i}-${key}`;

                return (
                    <div key={key} className="border rounded p-3 mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-3 mx-2">
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon
                                    className="text-primary"
                                    icon={icon}
                                    size="lg"
                                />
                                <h3 className="m-0 p-0 mt-1">{label}</h3>
                            </div>

                            <FontAwesomeIcon
                                id={tooltipId}
                                className="cursor-pointer text-warning"
                                icon={faRepeat}
                                size="lg"
                                onClick={isMutating ? undefined : () => reset(key)}
                            />

                            <UncontrolledTooltip target={tooltipId}>{t("reset")}</UncontrolledTooltip>
                        </div>

                        <hr className="p-0 m-0 mb-3" />

                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <div className="font-weight-bold">{t("updated-by")}:</div>
                                <div className="d-flex align-items-center gap-1">
                                    <Avatar user={item[key]?.updatedBy} size={25} />
                                    <div>{renderUserName(item[key]?.updatedBy)}</div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <p className="font-weight-bold m-0 p-0">{t("updated-at")}:</p>
                                <span className="m-0 p-0">{moment(item[key]?.updatedAt).format(dateFormat)}</span>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    ));
}

export default Overrides;
